import React, { useState } from "react";
import { BCMSRecord, useRoute, useBCMSContext, useWorkflowStep } from "@bcm-dev/bcms-vite";
import { getWorkflowSteps, getDefault } from "components/workflows/DisputeTransactionWorkflow";
import { Transaction } from "model";
import FormContainer, { FormButtonContainer, FormRow } from "components/shared/containers/FormContainer";
import CustomSelect, { CustomSelectItem } from "components/ui/CustomSelect";
import { UICancelButton, UIPrimaryButton } from "components/ui/UIButton";
import UITextInput from "components/ui/UITextInput";

import "./styles.css";

/**
 * @function DisputeReasonView
 * @param {STRING} merchantDescription - The description of which merchant made the transaction. 
 * @param {DECIMAL} disputeAmount - The amount of the transaction. 
 * @param {STRING} transactionDate - The date the transaction was made. 
 * @return {React.Component}
 * @exported true
 * @description This is the view shown if the “Yes, but I need to dispute the transaction.” option is selected on the first step of the workflow. 
 * @example
 * <DisputeReasonView merchantDescription="Description" disputeAmount="00.00" transactionDate="XX/XX/XXXX"/>
 */
export default function DisputeReasonView(props) {
  const globalController = useBCMSContext();
  BCMSRecord.globalControllerFor([ Transaction ], globalController);
  const workflowStep = useWorkflowStep(getDefault, getWorkflowSteps);
  const workflowStepState = workflowStep.getStepState;
  const [ state, setState ] = useState({...workflowStepState});
  const merchantDescription = state.transaction.merchant.description ?? "[merchantDescription]";
  const disputeAmount = state.transaction.amount.format() ?? "[disputeAmount]";
  const transactionDate = state.transaction.purchaseDate.format('M d, Y') ?? "[transactionDate]";

  const reasonChange = val => {
    setState({...state, disputeReason: val});
  }

  const onAdditionalCommentsChange = val => {
    setState({...state, disputeReasonAdditionalComments: val});
  }

  const submit = evt => {
    workflowStep.update(["disputeReason"], state.disputeReason === "" ? "Double bill" : state.disputeReason);
    workflowStep.update(["disputeReasonAdditionalComments"], state.disputeReasonAdditionalComments);
    workflowStep.workflow.nextStep();
  }

  const navigate = useRoute();
  const cancel = evt => {
    navigate(-1);
  }

  return (
    <FormContainer className="dispute-reason-view">
      <p>Select the reason you are disputing this transaction. Feel free to include additional comments to give us more context.</p>
      <div className="transaction-details">
        <FormRow>
          <p>Merchant Description</p>
          <p className="gray-text">{merchantDescription}</p>
        </FormRow>
        <FormRow>
          <p>Dispute Amount</p>
          <p className="gray-text">{disputeAmount}</p>
        </FormRow>
        <FormRow>
          <p>Transaction Date</p>
          <p className="gray-text">{transactionDate}</p>
        </FormRow>
      </div>
      <FormRow>
        <CustomSelect label="Reason*" value={state.disputeReason} onChange={reasonChange}>
          <CustomSelectItem>Double bill</CustomSelectItem>
          <CustomSelectItem>Credit not processed</CustomSelectItem>
          <CustomSelectItem>Merchandise was returned</CustomSelectItem>
          <CustomSelectItem>Transaction amount differs</CustomSelectItem>
          <CustomSelectItem>ATM cash dispute</CustomSelectItem>
          <CustomSelectItem>Paid by other means</CustomSelectItem>
          <CustomSelectItem>Canceled reservation</CustomSelectItem>
        </CustomSelect>
      </FormRow>
      <FormRow>
        <UITextInput placeholder="Additional Comments" value={state.disputeReasonAdditionalComments} onChange={onAdditionalCommentsChange} />
      </FormRow>
      <FormButtonContainer>
        <UIPrimaryButton text="Submit" onClick={submit} />
        <UICancelButton text="Cancel" onClick={cancel} />
      </FormButtonContainer>
    </FormContainer>
  );
}